(() => {
  const configElement = document.getElementById('grabbr-currency-config');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch {
    return;
  }

  const locale = config.locale || document.documentElement.lang || 'en';
  const useDanishBundle = config.country === 'DK' || /^da(?:-|$)/iu.test(locale);
  const currency = config.currency;
  // Liquid formats a known amount so these prices use the storefront's currency precision.
  const probeDigits = (config.moneyProbe || '').match(/[0-9٠-٩۰-۹]/gu)?.length;
  const fractionDigits = probeDigits === undefined ? null : probeDigits - 4;
  const precision = fractionDigits !== null && fractionDigits >= 0 && fractionDigits <= 3
    ? { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }
    : {};
  let formatter;
  try {
    formatter = new Intl.NumberFormat(useDanishBundle ? 'da-DK' : locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      ...precision,
    });
  } catch {
    return;
  }

  const factor = 10 ** formatter.resolvedOptions().maximumFractionDigits;
  const widgetSelector = `.bogos-volume-discount-widget[data-bundle-id="${config.offerId}"]`;
  const marqueeSelector = '[data-grabbr-currency-marquee]';
  const upsellSelector = '.bogos-pa-upsell-container[data-upsell-id="16160"]';
  const useBrazilianPortugueseFallback = /^pt[-_]br$/iu.test(locale);
  const brazilianPortugueseText = new Map([
    ['Bundle & Save', 'Compre junto e economize'],
    ['Full Price', 'Preço normal'],
    ['Most Popular', 'Mais popular'],
    ['Total price', 'Preço total'],
    ['ADD TO CART', 'ADICIONAR AO CARRINHO'],
    ['Choose length', 'Escolha o comprimento'],
    ['Variants', 'Variantes'],
    ['Item', 'Item'],
    ['per item', 'por item'],
    ['Subscription', 'Assinatura'],
    ['One-time purchase', 'Compra única'],
    ['Subscribe & save', 'Assine e economize'],
    ['🎁 Free Gift', '🎁 Brinde grátis'],
    ['FREE', 'GRÁTIS'],
    ['FREE SHIPPING Expiring in', 'Frete grátis termina em'],
    ['Expiring in', 'Termina em'],
    ['Days', 'Dias'],
    ['Hours', 'Horas'],
    ['Minutes', 'Minutos'],
    ['Seconds', 'Segundos'],
  ]);
  const accessibilityText = {
    en: ['Variants', 'Added to cart', 'Decrease quantity', 'Quantity', 'Increase quantity'],
    da: ['Varianter', 'Lagt i kurven', 'Sænk antal', 'Antal', 'Øg antal'],
    ar: ['خيارات المنتج', 'تمت الإضافة إلى سلة التسوق', 'تقليل الكمية', 'الكمية', 'زيادة الكمية'],
    de: ['Varianten', 'Zum Warenkorb hinzugefügt', 'Menge verringern', 'Menge', 'Menge erhöhen'],
    es: ['Variantes', 'Añadido al carrito', 'Reducir cantidad', 'Cantidad', 'Aumentar cantidad'],
    fr: ['Variantes', 'Ajouté au panier', 'Diminuer la quantité', 'Quantité', 'Augmenter la quantité'],
    hi: ['वेरिएंट', 'कार्ट में जोड़ा गया', 'मात्रा घटाएँ', 'मात्रा', 'मात्रा बढ़ाएँ'],
    id: ['Varian', 'Ditambahkan ke keranjang', 'Kurangi jumlah', 'Jumlah', 'Tambah jumlah'],
    it: ['Varianti', 'Aggiunto al carrello', 'Riduci quantità', 'Quantità', 'Aumenta quantità'],
    ja: ['バリエーション', 'カートに追加しました', '数量を減らす', '数量', '数量を増やす'],
    ko: ['옵션', '장바구니에 추가됨', '수량 줄이기', '수량', '수량 늘리기'],
    nl: ['Varianten', 'Toegevoegd aan winkelwagen', 'Aantal verlagen', 'Aantal', 'Aantal verhogen'],
    pl: ['Warianty', 'Dodano do koszyka', 'Zmniejsz ilość', 'Ilość', 'Zwiększ ilość'],
    pt: ['Variantes', 'Adicionado ao carrinho', 'Diminuir a quantidade', 'Quantidade', 'Aumentar a quantidade'],
    ro: ['Variante', 'Adăugat în coș', 'Scade cantitatea', 'Cantitate', 'Crește cantitatea'],
    ru: ['Варианты', 'Добавлено в корзину', 'Уменьшить количество', 'Количество', 'Увеличить количество'],
    sv: ['Varianter', 'Tillagd i varukorgen', 'Minska antal', 'Antal', 'Öka antal'],
    th: ['ตัวเลือกสินค้า', 'เพิ่มลงตะกร้าแล้ว', 'ลดจำนวน', 'จำนวน', 'เพิ่มจำนวน'],
    tr: ['Seçenekler', 'Sepete eklendi', 'Miktarı azalt', 'Miktar', 'Miktarı artır'],
    ur: ['اقسام', 'کارٹ میں شامل کر دیا گیا', 'مقدار کم کریں', 'مقدار', 'مقدار بڑھائیں'],
    vi: ['Biến thể', 'Đã thêm vào giỏ hàng', 'Giảm số lượng', 'Số lượng', 'Tăng số lượng'],
  };
  const accessibilityLocale = useDanishBundle ? 'da' : locale.toLowerCase().split(/[-_]/u)[0];
  const accessibilityLabels = accessibilityText[accessibilityLocale] || accessibilityText.en;
  const addingToCartText = {
    en: 'Adding to cart', da: 'Lægger i kurven', ar: 'جارٍ الإضافة إلى سلة التسوق',
    de: 'Wird zum Warenkorb hinzugefügt', es: 'Añadiendo al carrito', fr: 'Ajout au panier en cours',
    hi: 'कार्ट में जोड़ा जा रहा है', id: 'Menambahkan ke keranjang', it: 'Aggiunta al carrello in corso',
    ja: 'カートに追加中', ko: '장바구니에 추가 중', nl: 'Toevoegen aan winkelwagen',
    pl: 'Dodawanie do koszyka', pt: 'Adicionando ao carrinho', ro: 'Se adaugă în coș',
    ru: 'Добавление в корзину', sv: 'Lägger till i varukorgen', th: 'กำลังเพิ่มลงตะกร้า',
    tr: 'Sepete ekleniyor', ur: 'کارٹ میں شامل کیا جا رہا ہے', vi: 'Đang thêm vào giỏ hàng',
  };
  let variantLabelCounter = 0;
  const templates = new WeakMap();
  const numberPattern = '[0-9٠-٩۰-۹]+(?:[.,٫٬][0-9٠-٩۰-۹]+)*';
  const localCurrencyNames = [...new Set([
    currency,
    ...formatter.formatToParts(1).filter(part => part.type === 'currency').map(part => part.value),
  ])].map(name => name.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&')).join('|');
  const dollarNames = [
    'US\\s*\\$', 'USD\\b', '\\$', 'U\\.?S\\.?\\s+dollars?', 'American\\s+dollars?',
    'دولار(?:ات|ًا|ا)?(?:\\s+أمريكي(?:ة|اً|ا)?)?', 'अमेरिकी\\s+डॉलर',
    '米ドル', '미국\\s*달러', '달러', 'доллар(?:а|ов)?\\s+США',
    'ABD\\s+dolar[ıi]', 'ดอลลาร์สหรัฐ', 'امریکی\\s+ڈالر',
    'dólares?\\s+(?:estadounidenses|americanos)', 'dollars?\\s+(?:US|américains)', localCurrencyNames,
  ].join('|');
  const moneyPattern = new RegExp(
    `[\\u2066-\\u2069]*(?:미화\\s*${numberPattern}\\s*달러|(?:US\\s*\\$|USD\\s*|\\$|${localCurrencyNames})\\s*${numberPattern}|${numberPattern}\\s*(?:${dollarNames})|\\{\\{discountAmount\\}\\})[\\u2066-\\u2069]*`,
    'giu',
  );

  function sourceNumber(text) {
    const match = text.match(new RegExp(numberPattern, 'u'));
    if (!match) return null;
    let value = match[0].replace(/[٠-٩۰-۹]/gu, digit => {
      const code = digit.charCodeAt(0);
      return String(code >= 0x6f0 ? code - 0x6f0 : code - 0x660);
    }).replace(/٬/gu, '').replace(/٫/gu, '.');
    const comma = value.lastIndexOf(',');
    const dot = value.lastIndexOf('.');
    if (comma > dot) value = value.replace(/\./gu, '').replace(',', '.');
    else value = value.replace(/,/gu, '');
    const result = Number(value);
    return Number.isFinite(result) && result >= 0 ? result : null;
  }

  function savings(item) {
    if (!item) return null;
    const originalText = item.getAttribute('data-bogos-tier-original-price');
    const discountedText = item.getAttribute('data-bogos-tier-discount-price');
    if (originalText === null || discountedText === null || originalText === '' || discountedText === '') return null;
    const original = Number(originalText);
    const discounted = Number(discountedText);
    if (!Number.isFinite(original) || !Number.isFinite(discounted) || original < discounted || discounted < 0) return null;
    return (Math.round(original * factor) - Math.round(discounted * factor)) / factor;
  }

  function conversionRate() {
    if (currency === config.baseCurrency) return 1;
    const platformCurrency = window.Shopify?.currency;
    const rate = Number(platformCurrency?.rate);
    if (platformCurrency?.active === currency && Number.isFinite(rate) && rate > 0) return rate;
    return null;
  }

  function replaceText(element, amountForMatch, hideIfUnavailable = true) {
    if (!element) return;
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let node;
    let unavailable = false;
    while ((node = walker.nextNode())) {
      let state = templates.get(node);
      if (!state || node.nodeValue !== state.applied) {
        state = { source: node.nodeValue, applied: node.nodeValue };
        templates.set(node, state);
      }
      const output = state.source.replace(moneyPattern, match => {
        const amount = amountForMatch(match);
        if (amount === null || !Number.isFinite(amount) || amount < 0) {
          unavailable = true;
          return hideIfUnavailable ? match : '';
        }
        return `\u2068${formatter.format(amount)}\u2069`;
      });
      if (node.nodeValue !== output) node.nodeValue = output;
      state.applied = output;
    }
    element.toggleAttribute('data-grabbr-currency-unavailable', unavailable && hideIfUnavailable);
    element.setAttribute('data-grabbr-currency-ready', '');
    element.removeAttribute('data-grabbr-currency-pending');
  }

  function localizeBrazilianPortuguese(widget) {
    if (!useBrazilianPortugueseFallback) return;
    const walker = document.createTreeWalker(widget, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const original = node.nodeValue.trim();
      if (!original || node.parentElement?.closest('script,style')) continue;
      let translated = brazilianPortugueseText.get(original);
      if (translated === undefined) {
        translated = original.replace(/^Buy (\d+)$/u, 'Compre $1')
          .replace(/\bADD TO CART\b/gu, 'ADICIONAR AO CARRINHO')
          .replace(/\bSAVE(?=\s)/gu, 'ECONOMIZE')
          .replace(/\bFREE SHIPPING\b/gu, 'FRETE GRÁTIS');
      }
      if (translated !== original) node.nodeValue = node.nodeValue.replace(original, translated);
    }
    for (const element of widget.querySelectorAll('[aria-label]')) {
      const translated = brazilianPortugueseText.get(element.getAttribute('aria-label'));
      if (translated !== undefined) element.setAttribute('aria-label', translated);
    }
    widget.setAttribute('data-grabbr-bogos-locale', 'pt-BR');
  }

  function setPlainText(element, text) {
    if (element && element.textContent !== text) element.textContent = text;
  }

  function localizeAccessibility(root) {
    const lists = root.querySelectorAll('[role="listbox"]');
    if (lists.length) {
      let label = root.querySelector('[data-grabbr-bogos-variant-label]');
      if (!label) {
        label = document.createElement('span');
        label.hidden = true;
        label.id = `grabbr-bogos-variants-${++variantLabelCounter}`;
        label.setAttribute('data-grabbr-bogos-variant-label', '');
        root.append(label);
      }
      setPlainText(label, accessibilityLabels[0]);
      for (const list of lists) {
        list.setAttribute('aria-label', accessibilityLabels[0]);
        list.setAttribute('aria-labelledby', label.id);
      }
    }
    const status = root.querySelector('.bogos-vd-status');
    if (status?.textContent.trim() === 'Added to cart') setPlainText(status, accessibilityLabels[1]);
    else if (status?.textContent.trim() === 'Adding to cart') setPlainText(status, addingToCartText[accessibilityLocale] || addingToCartText.en);
    const quantitySelectors = ['.bogos-pa-qty-minus', '.bogos-pa-qty-value', '.bogos-pa-qty-plus'];
    quantitySelectors.forEach((selector, index) => {
      for (const element of root.querySelectorAll(selector)) element.setAttribute('aria-label', accessibilityLabels[index + 2]);
    });
  }

  function localizeDanish(widget) {
    if (!useDanishBundle) return;
    setPlainText(widget.querySelector('.bogos-volume-discount-widget-title'), 'Køb flere og spar');
    setPlainText(widget.querySelector('.bogos-rc-text'), 'Gratis fragt slutter om');
    for (const item of widget.querySelectorAll('.bogos-volume-discount-item')) {
      const container = item.closest('.bogos-volume-discount_item-container');
      const quantity = container?.querySelectorAll('.bogos-volume-discount-variant-item').length;
      if (quantity) setPlainText(item.querySelector('.bogos-volume-discount-title'), `Køb ${quantity}`);
      const amount = savings(item);
      if (amount !== null) {
        const label = amount > 0 ? `SPAR \u2068${formatter.format(amount)}\u2069 + GRATIS FRAGT` : 'Normalpris';
        setPlainText(item.querySelector('.bogos-volume-discount-label'), label);
      }
      const tag = item.querySelector('.bogos-volume-discount-tag');
      if (tag?.textContent.trim()) setPlainText(tag, 'Mest populær');
      for (const [selector, attribute] of [
        ['.bogos-volume-discount-item-discount-price', 'data-bogos-tier-discount-price'],
        ['.bogos-volume-discount-item-original-price', 'data-bogos-tier-original-price'],
      ]) {
        const price = item.querySelector(selector);
        const value = item.getAttribute(attribute);
        if (price?.textContent.trim() && value !== null && value !== '' && Number.isFinite(Number(value))) {
          setPlainText(price, formatter.format(Number(value)));
        }
      }
    }
    for (const element of widget.querySelectorAll('.bogos-volume-discount-item-select-button')) setPlainText(element, 'Vælg længde');
    for (const element of widget.querySelectorAll('.bogos-vl-shipping-label')) setPlainText(element, 'GRATIS FRAGT');
    for (const element of widget.querySelectorAll('.bogos-volume-discount-variant-item-title > span')) setPlainText(element, 'Vare');
    const metricLengths = {
      '55472798531923': '60 cm',
      '55472798564691': '160 cm',
    };
    for (const element of widget.querySelectorAll('[data-variant-id]')) {
      const label = metricLengths[element.getAttribute('data-variant-id')];
      if (!label) continue;
      if (element.matches('input[role="combobox"]')) {
        if (element.value !== label) element.value = label;
        if (element.getAttribute('data-bogos-selected') !== label) element.setAttribute('data-bogos-selected', label);
      } else if (element.matches('.bogos-vl-select-variant-option')) setPlainText(element, label);
    }
    setPlainText(widget.querySelector('.bogos-volume-discount-total-title > span'), 'Samlet pris');
    const button = widget.querySelector('.bogos-volume-discount-button-add');
    setPlainText(button?.querySelector('span:first-child'), 'LÆG I KURV');
    const selected = widget.querySelector('.bogos-volume-discount-tier-input:checked')?.closest('.bogos-volume-discount-item');
    const amount = savings(selected);
    if (amount !== null) setPlainText(button?.querySelector('span:nth-child(2)'), amount > 0 ? `SPAR \u2068${formatter.format(amount)}\u2069 + GRATIS FRAGT` : 'Normalpris');
    for (const [selector, attribute] of [
      ['.bogos-volume-discount-discount-price', 'data-bogos-tier-discount-price'],
      ['.bogos-volume-discount-origin-price', 'data-bogos-tier-original-price'],
    ]) {
      const price = widget.querySelector(selector);
      const value = selected?.getAttribute(attribute);
      if (price?.textContent.trim() && value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value))) {
        setPlainText(price, formatter.format(Number(value)));
      }
    }
    widget.setAttribute('lang', 'da');
    widget.setAttribute('dir', 'ltr');
    widget.setAttribute('data-grabbr-bogos-locale', 'da');
  }

  function formatCurrencyPrecision(widget) {
    if (factor === 100) return;
    const selected = widget.querySelector('.bogos-volume-discount-tier-input:checked')?.closest('.bogos-volume-discount-item');
    const groups = [
      ...[...widget.querySelectorAll('.bogos-volume-discount-item')].map(item => [item, item, '.bogos-volume-discount-item-discount-price', '.bogos-volume-discount-item-original-price']),
      [widget, selected, '.bogos-volume-discount-discount-price', '.bogos-volume-discount-origin-price'],
    ];
    for (const [root, item, discountedSelector, originalSelector] of groups) {
      for (const [selector, attribute] of [[discountedSelector, 'data-bogos-tier-discount-price'], [originalSelector, 'data-bogos-tier-original-price']]) {
        const element = root.querySelector(selector);
        const value = item?.getAttribute(attribute);
        if (element?.textContent.trim() && value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value))) {
          setPlainText(element, formatter.format(Number(value)));
        }
      }
    }
  }

  function update() {
    const rate = conversionRate();
    for (const marquee of document.querySelectorAll(marqueeSelector)) {
      for (const text of marquee.querySelectorAll('[data-grabbr-currency-text]')) {
        replaceText(text, match => {
          const amount = sourceNumber(match);
          return amount === null || rate === null ? null : amount * rate;
        });
      }
      marquee.setAttribute('data-grabbr-display-currency', currency);
    }

    for (const widget of document.querySelectorAll(widgetSelector)) {
      if (useDanishBundle) localizeDanish(widget);
      else localizeBrazilianPortuguese(widget);
      formatCurrencyPrecision(widget);
      localizeAccessibility(widget);
      for (const item of widget.querySelectorAll('.bogos-volume-discount-item')) {
        const amount = savings(item);
        replaceText(item.querySelector('.bogos-volume-discount-label'), () => amount);
      }
      const selected = widget.querySelector('.bogos-volume-discount-tier-input:checked');
      const amount = savings(selected?.closest('.bogos-volume-discount-item'));
      replaceText(widget.querySelector('.bogos-volume-discount-button-add'), () => amount, false);
      widget.setAttribute('data-grabbr-display-currency', currency);
    }
    for (const upsell of document.querySelectorAll(upsellSelector)) {
      localizeAccessibility(upsell);
      const product = upsell.querySelector('[data-product-id="11003754283347"]');
      const value = product?.getAttribute('data-variant-price');
      if ((useDanishBundle || factor !== 100) && value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value))) {
        setPlainText(product.querySelector('.bogos-pa-price-sale'), formatter.format(Number(value)));
      }
      if (useDanishBundle) {
        setPlainText(product?.querySelector('.bogos-pa-product-name'), 'Tilføj fragtbeskyttelse');
        const image = product?.querySelector('img');
        if (image) image.setAttribute('alt', 'Tilføj fragtbeskyttelse');
        upsell.setAttribute('lang', 'da');
        upsell.setAttribute('dir', 'ltr');
      }
    }
  }

  let scheduled = false;
  let observer;
  const observerOptions = {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['data-bogos-tier-original-price', 'data-bogos-tier-discount-price', 'data-variant-id', 'aria-expanded'],
  };

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      observer.disconnect();
      update();
      observer.observe(document.body, observerOptions);
    });
  }

  function relevant(record) {
    const element = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
    if ((useBrazilianPortugueseFallback || useDanishBundle) && element?.closest(widgetSelector)) return true;
    if (record.type === 'attributes' && record.attributeName === 'aria-expanded' && element?.closest(widgetSelector)) return true;
    if (element?.closest(`${widgetSelector} .bogos-vd-status,${upsellSelector}`)) return true;
    if (element?.closest(`${marqueeSelector},${widgetSelector} .bogos-volume-discount-item,${widgetSelector} .bogos-volume-discount-button-add`)) return true;
    if (record.type !== 'childList') return false;
    return [...record.addedNodes].some(node => node.nodeType === Node.ELEMENT_NODE && (
      node.matches(`${marqueeSelector},${widgetSelector},${upsellSelector}`) || node.querySelector(`${marqueeSelector},${widgetSelector},${upsellSelector}`)
    ));
  }

  function start() {
    update();
    document.documentElement.setAttribute('data-grabbr-currency-active', '');
    observer = new MutationObserver(records => {
      if (records.some(relevant)) schedule();
    });
    observer.observe(document.body, observerOptions);
    document.addEventListener('change', event => {
      if (event.target.closest(widgetSelector)) schedule();
    });
    document.addEventListener('shopify:section:load', schedule);
    window.addEventListener('pageshow', schedule);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
